'use client';

import { useEffect, useState } from 'react';
import {
  isHistoricFormForbiddenError,
  isHistoricFormUnsupportedVersionError,
  loadHistoricFormData,
  type HistoricFormData,
} from '@/lib/historic-form-loader';
import type { HistoricTaskInstance } from '@/lib/camunda/history-types';

interface UseHistoricFormOptions {
  task: HistoricTaskInstance;
  formKey: string | null;
  processInstanceId: string;
  enabled: boolean;
}

interface HistoricFormError {
  code: 'HISTORIC_FORM_UNSUPPORTED_VERSION' | 'FORBIDDEN' | 'TECHNICAL';
  message: string;
}

interface UseHistoricFormResult {
  data: HistoricFormData | null;
  loading: boolean;
  error: HistoricFormError | null;
  reload: () => void;
}

export function useHistoricForm({
  task,
  formKey,
  processInstanceId,
  enabled,
}: UseHistoricFormOptions): UseHistoricFormResult {
  const [data, setData] = useState<HistoricFormData | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<HistoricFormError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = () => {
    setReloadToken((current) => current + 1);
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await loadHistoricFormData(task, processInstanceId, formKey);
        if (!cancelled) {
          setData(result);
        }
      } catch (loadError) {
        if (!cancelled) {
          setData(null);
          setError({
            code: isHistoricFormUnsupportedVersionError(loadError)
              ? 'HISTORIC_FORM_UNSUPPORTED_VERSION'
              : isHistoricFormForbiddenError(loadError)
                ? 'FORBIDDEN'
              : 'TECHNICAL',
            message: loadError instanceof Error
              ? loadError.message
              : 'Error al cargar el formulario histórico',
          });
        }
      }
      if (!cancelled) {
        setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [task, processInstanceId, formKey, enabled, reloadToken]);

  if (!enabled) {
    return { data: null, loading: false, error: null, reload };
  }
  return { data, loading, error, reload };
}
